import React, { useCallback, useEffect, useState } from 'react';
import { prescriptionAPI, patientAPI } from '../../services/api';
import FilterBar from '../../components/common/FilterBar';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLOR = {
  ACTIVE: '#10b981', COMPLETED: '#94335a', DISCONTINUED: '#ef4444',
  ON_HOLD: '#f59e0b', VOIDED: '#8b7681',
};

const ROUTES = ['ORAL', 'IV', 'IM', 'SC', 'TOPICAL', 'INHALATION', 'SUBLINGUAL', 'NASAL', 'OPHTHALMIC'];
const FREQUENCIES = ['ONCE_DAILY', 'TWICE_DAILY', 'THREE_TIMES_DAILY', 'FOUR_TIMES_DAILY',
  'EVERY_8_HOURS', 'EVERY_12_HOURS', 'WEEKLY', 'AS_NEEDED', 'STAT'];

const FILTERS = [
  { key: 'status', label: 'Status', type: 'select', options: ['ACTIVE', 'COMPLETED', 'DISCONTINUED', 'ON_HOLD'] },
  { key: 'search', label: 'Medication', type: 'text', placeholder: 'Search medication…' },
];

const DEF_FILTER = { status: '', search: '' };
const DEF_FORM = {
  patient_id: '', medication_name: '', medication_code: '', snomed_code: '',
  dose: '', dose_unit: 'mg', route: 'ORAL', frequency: 'ONCE_DAILY',
  start_date: '', end_date: '', duration_days: '', refills_allowed: 0, instructions: '',
};

export default function Prescriptions() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isPatientOnly = roles.includes('PATIENT') && !roles.some(r => ['ADMIN', 'DOCTOR', 'NURSE', 'BILLING'].includes(r));
  const [items, setItems]         = useState([]);
  const [patients, setPatients]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filters, setFilters]     = useState(DEF_FILTER);
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem]     = useState(null);   // for status/details edit
  const [discItem, setDiscItem]     = useState(null);   // for discontinue confirm
  const [form, setForm]             = useState(DEF_FORM);
  const [editStatus, setEditStatus] = useState('');
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  const PER = 20;

  const load = useCallback(() => {
    setLoading(true);
    const params = { skip: (page - 1) * PER, limit: PER };
    if (filters.status) params.status = filters.status;
    prescriptionAPI.list(params)
      .then(r => { setItems(r.data || []); setTotal(r.meta?.total || 0); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filters.status, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isPatientOnly) return; // patient list is staff-only; patient view is read-only
    patientAPI.list({ limit: 200 })
      .then(r => setPatients(r.data || []))
      .catch(() => {});
  }, [isPatientOnly]);

  const filtered = items.filter(p =>
    !filters.search || (p.medication_name || '').toLowerCase().includes(filters.search.toLowerCase())
  );

  // ── Create ────────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    // Defence-in-depth: patients can only create for themselves even if the form
    // field is tampered. The backend enforces this too, but belt-and-suspenders.
    const effectivePatientId = isPatientOnly ? user?.party_id : form.patient_id;
    if (!effectivePatientId || !form.medication_name) { setError('Patient and medication name are required'); return; }
    setSaving(true); setError('');
    try {
      await prescriptionAPI.create({
        patient_id: effectivePatientId,
        medication_name: form.medication_name,
        medication_code: form.medication_code || undefined,
        snomed_code: form.snomed_code || undefined,
        dose: form.dose || undefined,
        dose_unit: form.dose_unit || undefined,
        route: form.route || undefined,
        frequency: form.frequency || undefined,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
        duration_days: form.duration_days ? Number(form.duration_days) : undefined,
        refills_allowed: Number(form.refills_allowed) || 0,
        instructions: form.instructions || undefined,
      });
      setShowCreate(false); setForm(DEF_FORM); load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create prescription');
    } finally { setSaving(false); }
  };

  // ── Edit status ───────────────────────────────────────────────────────────────
  const handleEditSave = async () => {
    if (!editStatus) return;
    setSaving(true); setError('');
    try {
      await prescriptionAPI.updateStatus(editItem.prescription_id, editStatus);
      setEditItem(null); load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  // ── Discontinue ────────────────────────────────────────────────────────────────
  const handleDiscontinue = async () => {
    setSaving(true);
    try {
      await prescriptionAPI.discontinue(discItem.prescription_id);
      setDiscItem(null); load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to discontinue');
    } finally { setSaving(false); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const totalPages = Math.max(1, Math.ceil(total / PER));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#2b1b22', margin: 0 }}>{isPatientOnly ? 'My Prescriptions' : 'Prescriptions'}</h1>
          <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>
            {isPatientOnly ? 'Your prescriptions, as issued by your care team.' : 'FHIR R4 MedicationRequest · RxNorm / DCI coding · Full lifecycle management'}
          </p>
        </div>
        {!isPatientOnly && <button onClick={() => { setShowCreate(true); setError(''); setForm(DEF_FORM); }} style={{
          padding: '9px 18px', background: '#94335a', color: '#fff',
          border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          + New Prescription
        </button>}
      </div>

      {/* Standards */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['FHIR R4 MedicationRequest', 'RxNorm Codes', 'DCI / INN Names', 'WHO Essential Medicines'].map(s => (
          <span key={s} style={{ padding: '4px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 20, fontSize: 11, fontWeight: 600, color: '#16a34a' }}>
            ✓ {s}
          </span>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="hhms-cards-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {['ACTIVE', 'COMPLETED', 'ON_HOLD', 'DISCONTINUED'].map(s => {
          const count = items.filter(p => p.status === s).length;
          return (
            <div key={s} style={{
              background: '#fff', borderRadius: 10, padding: '14px 18px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.07)', borderTop: `3px solid ${STATUS_COLOR[s] || '#a99aa0'}`,
            }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#2b1b22' }}>{count}</div>
              <div style={{ fontSize: 11, color: '#8b7681' }}>{s.replace(/_/g, ' ')}</div>
            </div>
          );
        })}
      </div>

      <FilterBar
        filters={FILTERS}
        values={filters}
        onChange={(k, v) => { setFilters(p => ({ ...p, [k]: v })); setPage(1); }}
        onClear={() => { setFilters(DEF_FILTER); setPage(1); }}
      />

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        {loading ? (
          <div style={{ color: '#a99aa0', fontSize: 13 }}>Loading prescriptions…</div>
        ) : filtered.length === 0 ? (
          <div style={{ color: '#a99aa0', textAlign: 'center', padding: '40px 0', fontSize: 13 }}>
            No prescriptions found.
          </div>
        ) : (
          <div className="hhms-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Medication', 'RxNorm', 'Dose', 'Route', 'Frequency', 'Valid Until', 'Status', ...(isPatientOnly ? [] : ['Actions'])].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#8b7681', fontWeight: 600, borderBottom: '2px solid #f1f5f9', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.prescription_id} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '9px 12px', fontWeight: 700, color: '#2b1b22' }}>{p.medication_name}</td>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#8b5cf6', fontSize: 11 }}>{p.rxnorm_code || '—'}</td>
                    <td style={{ padding: '9px 12px', color: '#5e4a53' }}>{p.dose} {p.unit}</td>
                    <td style={{ padding: '9px 12px', color: '#5e4a53' }}>{p.route || '—'}</td>
                    <td style={{ padding: '9px 12px', color: '#5e4a53' }}>{(p.frequency || '—').replace(/_/g, ' ')}</td>
                    <td style={{ padding: '9px 12px', color: '#5e4a53' }}>{p.valid_until ? new Date(p.valid_until).toLocaleDateString() : '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                        background: `${STATUS_COLOR[p.status] || '#a99aa0'}20`,
                        color: STATUS_COLOR[p.status] || '#a99aa0',
                      }}>
                        {p.status || '—'}
                      </span>
                    </td>
                    {!isPatientOnly && <td style={{ padding: '9px 12px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => { setEditItem(p); setEditStatus(p.status); setError(''); }}
                          style={{ padding: '4px 10px', background: '#fdeef5', color: '#0369a1', border: '1px solid #f0c8da', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                        >
                          Edit
                        </button>
                        {p.status !== 'DISCONTINUED' && p.status !== 'COMPLETED' && (
                          <button
                            onClick={() => setDiscItem(p)}
                            style={{ padding: '4px 10px', background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                          >
                            Discontinue
                          </button>
                        )}
                      </div>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ece3e7', background: '#fff', cursor: 'pointer', fontSize: 12 }}>
              ← Prev
            </button>
            <span style={{ padding: '6px 10px', fontSize: 12, color: '#8b7681' }}>Page {page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ece3e7', background: '#fff', cursor: 'pointer', fontSize: 12 }}>
              Next →
            </button>
          </div>
        )}
      </div>

      {/* ── Create Modal ─────────────────────────────────────────────────────── */}
      {showCreate && (
        <Modal title="New Prescription" onClose={() => setShowCreate(false)} fullscreen>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Patient *</label>
            <select value={form.patient_id} onChange={e => f('patient_id', e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
              <option value="">Select patient…</option>
              {patients.map(p => <option key={p.party_id} value={p.party_id}>{p.name}</option>)}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            {[
              ['Medication Name *', 'medication_name', 'e.g. Amoxicillin', 'text'],
              ['RxNorm Code', 'medication_code', 'e.g. 723', 'text'],
              ['Dose', 'dose', 'e.g. 500', 'text'],
              ['Unit', 'dose_unit', 'mg', 'text'],
              ['Duration (days)', 'duration_days', '7', 'number'],
              ['Refills Allowed', 'refills_allowed', '0', 'number'],
              ['Start Date', 'start_date', '', 'date'],
              ['End Date', 'end_date', '', 'date'],
            ].map(([label, key, ph, type]) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>{label}</label>
                <input type={type} value={form[key]} onChange={e => f(key, e.target.value)} placeholder={ph}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            ))}

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Route</label>
              <select value={form.route} onChange={e => f('route', e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
                {ROUTES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Frequency</label>
              <select value={form.frequency} onChange={e => f('frequency', e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
                {FREQUENCIES.map(fr => <option key={fr} value={fr}>{fr.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Instructions</label>
            <textarea value={form.instructions} onChange={e => f('instructions', e.target.value)} rows={3}
              placeholder="e.g. Take with food"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={() => setShowCreate(false)} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#5e4a53' }}>Cancel</button>
            <button onClick={handleCreate} disabled={saving} style={{ flex: 2, padding: 10, background: saving ? '#a99aa0' : '#94335a', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
              {saving ? 'Saving…' : 'Create Prescription'}
            </button>
          </div>
          </div>
        </Modal>
      )}

      {/* ── Edit Status Modal ─────────────────────────────────────────────────── */}
      {editItem && (
        <Modal title={`Edit — ${editItem.medication_name}`} onClose={() => setEditItem(null)} width={380}>
          {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Status</label>
            <select value={editStatus} onChange={e => setEditStatus(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
              {['ACTIVE', 'COMPLETED', 'ON_HOLD', 'DISCONTINUED'].map(s => (
                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setEditItem(null)} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#5e4a53' }}>Cancel</button>
            <button onClick={handleEditSave} disabled={saving} style={{ flex: 2, padding: 10, background: saving ? '#a99aa0' : '#94335a', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
              {saving ? 'Saving…' : 'Update Status'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Discontinue Confirm ───────────────────────────────────────────────── */}
      {discItem && (
        <ConfirmDialog
          message={`Discontinue "${discItem.medication_name}"?`}
          detail="This will set the prescription status to DISCONTINUED. The record is kept for audit."
          confirmLabel="Discontinue"
          loading={saving}
          onConfirm={handleDiscontinue}
          onCancel={() => setDiscItem(null)}
        />
      )}
    </div>
  );
}
